import { Controller } from '@hotwired/stimulus';
import { Modal } from 'bootstrap';

// Based off https://github.com/julesbravo/exit-intent-js
// ------------------------------------------------------
// Ported to Stimulus and enhanced with additional triggers and a feedback form.

export default class extends Controller {
  static values = {
    modalId: String,
    // Trigger: fire after this many ms on the page (0 = disabled)
    timeOnPage: { type: Number, default: 0 },
    // Trigger: fire after this many ms of inactivity (0 = disabled)
    idleTime: { type: Number, default: 0 },
    // Trigger: fire this many ms after mouse leaves the window (0 = disabled)
    mouseLeaveDelay: { type: Number, default: 1000 },
    // Trigger: fire when the tab becomes hidden
    tabChange: { type: Boolean, default: true },
    // Trigger: fire when the window loses focus (e.g. cursor moves to browser chrome)
    windowBlur: { type: Boolean, default: true },
    // Trigger: minimum px scrolled upward within the check interval to fire
    scrollUpThresholdMobile: { type: Number, default: 200 },
    scrollUpThresholdDesktop: { type: Number, default: 400 },
    // How often (ms) to sample scroll position for upward scroll detection
    scrollUpInterval: { type: Number, default: 100 },
    // Screen width (px) below which the device is considered mobile
    mobileBreakpoint: { type: Number, default: 768 },
    // Fire immediately once this many page views have been recorded (0 = disabled)
    pageViewsToTrigger: { type: Number, default: 0 },
    pageViewsStorageKey: { type: String, default: 'exit-intent-page-views' },
    debug: { type: Boolean, default: false },
    halfPlanUrl: { type: String, default: '' },
  };

  static targets = ['initialState', 'recipeState', 'lowerPriceState', 'otherState', 'thankYouState', 'textInput', 'charCount'];

  connect() {
    this._shown = false;
    this._modal = null;
    this._reason = null;
    this._timers = [];
    this._listeners = [];
    this._mouseLeaveTimer = null;
    this._idleTimer = null;
    this._scrollInterval = null;

    const modalEl = document.getElementById(this.modalIdValue);
    if (modalEl) {
      this._modal = new Modal(modalEl);
      modalEl.addEventListener('hidden.bs.modal', () => {
        this._reason = null;
        this._showState(this.initialStateTarget);
      });
    }

    this._incrementPageViews();

    if (this._isSessionComplete()) {
      this._log('Session already completed — triggers disabled');
      return;
    }

    this._setupTriggers();
  }

  disconnect() {
    this._timers.forEach(clearTimeout);
    this._listeners.forEach(({ el, type, fn }) => el.removeEventListener(type, fn));
    if (this._mouseLeaveTimer) clearTimeout(this._mouseLeaveTimer);
    if (this._idleTimer) clearTimeout(this._idleTimer);
    if (this._scrollInterval) clearInterval(this._scrollInterval);
  }

  // Called by the "Leave anyway" button in the modal
  leave() {
    if (this._modal) this._modal.hide();
    history.back();
  }

  // --- State management ---

  back() {
    this._reason = null;
    this.textInputTargets.forEach((el) => (el.value = ''));
    this.charCountTargets.forEach((el) => (el.textContent = 500));
    this._showState(this.initialStateTarget);
  }

  proceed() {
    const radio = this.element.querySelector('input[name="exit_intent_reason"]:checked');
    if (!radio) return;

    this._reason = radio.value;

    if (this._reason === 'samples' || (this._reason === 'lower_price' && this.halfPlanUrlValue)) {
      this.submitFeedback();
      return;
    }

    const stateTargets = {
      recipe: this.recipeStateTarget,
      lower_price: this.lowerPriceStateTarget,
      other: this.otherStateTarget,
    };
    this._showState(stateTargets[this._reason]);
  }

  submitFeedback() {
    const reason = this._reason;
    const textInput = this.textInputTargets.find((el) => !el.closest('.d-none'));
    const response = textInput?.value?.trim() || null;

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    fetch('/account/exit_intent', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ reason, response }),
    })
      .then(() => {
        if (reason === 'lower_price') {
          window.posthog?.capture('exit_intent_lower_price_submitted');
          if (this.halfPlanUrlValue) {
            this._markSessionComplete();
            window.location = this.halfPlanUrlValue;
            return;
          }
        }
        this._markSessionComplete();
        this._showState(this.thankYouStateTarget);
      })
      .catch(() => {
        this._markSessionComplete();
        this._showState(this.thankYouStateTarget);
      });
  }

  updateCharCount(event) {
    this.charCountTargets.forEach((el) => (el.textContent = 500 - event.target.value.length));
  }

  _showState(activeTarget) {
    [
      this.initialStateTarget,
      this.recipeStateTarget,
      this.lowerPriceStateTarget,
      this.otherStateTarget,
      this.thankYouStateTarget,
    ].forEach((el) => el.classList.toggle('d-none', el !== activeTarget));
  }

  // --- Setup ---

  _setupTriggers() {
    if (this.pageViewsToTriggerValue > 0 && this._getPageViews() >= this.pageViewsToTriggerValue) {
      this._log(`Page view threshold reached (${this._getPageViews()})`);
      this._trigger('pageViews');
    }

    if (this.timeOnPageValue > 0) {
      this._timers.push(setTimeout(() => this._trigger('timeOnPage'), this.timeOnPageValue));
    }

    if (this.idleTimeValue > 0) {
      this._setupIdleDetection();
    }

    if (this.mouseLeaveDelayValue > 0) {
      this._setupMouseLeave();
    }

    if (this.tabChangeValue) {
      this._setupTabChange();
    }

    if (this.windowBlurValue) {
      this._setupWindowBlur();
    }

    this._setupScrollUp();
  }

  _setupIdleDetection() {
    const reset = () => {
      if (this._idleTimer) clearTimeout(this._idleTimer);
      this._idleTimer = setTimeout(() => this._trigger('idleTime'), this.idleTimeValue);
    };

    ['mousemove', 'keydown', 'mousedown', 'touchstart'].forEach((type) => {
      window.addEventListener(type, reset);
      this._listeners.push({ el: window, type, fn: reset });
    });

    reset();
  }

  _setupMouseLeave() {
    const onMouseOut = (e) => {
      if (!e.relatedTarget) {
        this._mouseLeaveTimer = setTimeout(() => this._trigger('mouseLeave'), this.mouseLeaveDelayValue);
      }
    };

    const onMouseOver = () => {
      if (this._mouseLeaveTimer) clearTimeout(this._mouseLeaveTimer);
    };

    window.addEventListener('mouseout', onMouseOut);
    window.addEventListener('mouseover', onMouseOver);
    this._listeners.push(
      { el: window, type: 'mouseout', fn: onMouseOut },
      { el: window, type: 'mouseover', fn: onMouseOver }
    );
  }

  _setupTabChange() {
    const fn = () => {
      if (document.visibilityState === 'hidden') this._trigger('tabChange');
    };

    document.addEventListener('visibilitychange', fn);
    this._listeners.push({ el: document, type: 'visibilitychange', fn });
  }

  _setupWindowBlur() {
    const fn = () => {
      setTimeout(() => {
        const active = document.activeElement;
        if (active?.tagName === 'IFRAME' && document.contains(active)) {
          this._log('Window blur ignored — focus moved to child iframe');
          return;
        }
        this._trigger('windowBlur');
      }, 0);
    };

    window.addEventListener('blur', fn);
    this._listeners.push({ el: window, type: 'blur', fn });
  }

  _setupScrollUp() {
    const threshold = this._isMobile()
      ? this.scrollUpThresholdMobileValue
      : this.scrollUpThresholdDesktopValue;

    if (threshold <= 0) return;

    this._log(`Scroll detection active (${this._isMobile() ? 'mobile' : 'desktop'}, threshold: ${threshold}px)`);

    let lastScrollY = window.scrollY;

    this._scrollInterval = setInterval(() => {
      const current = window.scrollY;
      const delta = lastScrollY - current;

      if (delta >= threshold) {
        this._log(`Scroll up: ${delta}px`);
        this._trigger('scrollUp');
      }

      lastScrollY = current;
    }, this.scrollUpIntervalValue);
  }

  // --- Trigger ---

  _trigger(reason) {
    if (this._shown) return;
    this._log(`Triggered by: ${reason}`);
    this.dispatch('triggered', { detail: { reason } });

    this._showModal();
  }

  _showModal() {
    if (this._shown || !this._modal) return;
    this._shown = true;

    const modalEl = document.getElementById(this.modalIdValue);
    modalEl?.addEventListener('shown.bs.modal', () => {
      this._markSessionComplete();
      window.posthog?.capture('exit_intent_modal_viewed');
    }, { once: true });

    this._modal.show();
  }

  // --- Page views ---

  _getPageViews() {
    try {
      const n = parseInt(localStorage.getItem(this.pageViewsStorageKeyValue), 10);
      return isNaN(n) ? 0 : n;
    } catch {
      return 0;
    }
  }

  _setPageViews(count) {
    try {
      localStorage.setItem(this.pageViewsStorageKeyValue, String(count));
    } catch {
      /* localStorage unavailable */
    }
  }

  _incrementPageViews() {
    const updated = this._getPageViews() + 1;
    this._setPageViews(updated);
    return updated;
  }

  // --- Utilities ---

  _isMobile() {
    return window.innerWidth <= this.mobileBreakpointValue;
  }

  _sessionKey() {
    return `exit-intent-complete-${this.modalIdValue}`;
  }

  _isSessionComplete() {
    try {
      return sessionStorage.getItem(this._sessionKey()) === '1';
    } catch {
      return false;
    }
  }

  _markSessionComplete() {
    try {
      sessionStorage.setItem(this._sessionKey(), '1');
    } catch (e) {
      this._log(`sessionStorage unavailable: ${e}`);
    }
  }

  _log(message) {
    if (this.debugValue) console.log(`[exit-intent-modal] ${message}`);
  }
}
